import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  EMPTY_PLAYBACK_STATE,
  PlaybackController,
  PlaybackState,
} from '../../shared/contracts/playback-controller';

/**
 * One row in the search-history drawer: a vessel a worldwide search located,
 * whether placed this session or re-hydrated from the sensor's `vesselHistory`.
 *
 * Lives HERE, beside its one writer, since the phase-0 extraction
 * (REFACTOR-PLAN.md §5.4) — it is the row type of `VoyageVesselState.
 * locatedVessels`, not part of the operator-agnostic contract.
 */
export interface LocatedVesselRow {
  mmsi: string;
  /** Broadcast name, or null for an anonymous hull (the row shows the MMSI). */
  name: string | null;
  /** ISO/UTC stamp of the fix the marker is drawn at. */
  timeUtc: string | null;
  /**
   * Fix older than 48 h — drawn hollow and captioned "last known", never
   * "latest". A six-day-old position must not wear the authority of a
   * twelve-minute-old one.
   */
  stale: boolean;
  /** Persisted in the sensor's vesselHistory — survives a reload. */
  kept: boolean;
  /** The search text that found it, when known — the row's provenance. */
  query?: string | null;
}

/**
 * The located-vessels state — the generic contract plus the one field only
 * this controller writes (phase 0 extraction, REFACTOR-PLAN.md §5.4).
 */
export interface VoyageVesselState extends PlaybackState {
  /**
   * Every vessel a search has put on the map (empty = none). Drives the single
   * search-history control: one button, one drawer listing them all — the
   * per-vessel toolbar it replaced narrated one hull with seven controls.
   *
   * No default in the empty state, deliberately: undefined and [] both render
   * as "no section", and the pre-extraction contract never defaulted it either.
   */
  locatedVessels?: LocatedVesselRow[];
}

/**
 * Voyage Radar located-vessels controller — drives the single search-history
 * control (one button, one drawer listing every vessel a worldwide search put
 * on the map).
 *
 * Deliberately separate from the track and playback controllers: those own
 * clocks and transports, this owns exactly one fact — the list of located
 * vessels — plus the two per-row actions (center, take off the map). Everything
 * else a located vessel supports goes through the normal map grammar: clicking its
 * marker opens the popup and makes it the selection, which is what the
 * ordinary Trace button then acts on.
 *
 * Division of labour, same as the other two: this owns the drawer state, the
 * map controller owns Leaflet. Both actions are callbacks the map controller
 * assigns in `attach()` (the `onDimOthersToggle` pattern), so this file never
 * imports a map type.
 *
 * The transport contract is implemented as no-ops: a list of points is not a
 * timeline, and `PlaybackController` is the price of riding the shared toolbar
 * host.
 */
@Injectable({ providedIn: 'root' })
export class VoyageVesselController extends PlaybackController {
  private _state$ = new BehaviorSubject<VoyageVesselState>({ ...EMPTY_PLAYBACK_STATE });
  readonly state$: Observable<VoyageVesselState> = this._state$.asObservable();

  // ── Callbacks the map controller wires in attach() ──────────────────────────
  onCenterVessel: ((mmsi: string) => void) | null = null;
  /**
   * Take the marker down, keep the saved record. This drawer only ever
   * lists vessels that are ON the map, so clearing one from it is a statement
   * about the map — the permanent delete lives in the Saved Vessels panel, where
   * the row survives the marker.
   */
  onTakeOffMap: ((mmsi: string) => void) | null = null;
  /**
   * The same act over every listed vessel at once — never a delete, exactly as
   * above. Its own seam rather than a loop over `onTakeOffMap` because the map
   * controller's `takeAllLocatedOffMap` iterates a COPY of its keys: taking one
   * off mutates the map that a naive loop here would be walking.
   */
  onClearAll: (() => void) | null = null;

  // ── State written by the map controller ─────────────────────────────────────
  /** The search-history control renders iff this is non-empty. */
  setLocatedVessels(locatedVessels: LocatedVesselRow[]): void {
    this.patch({ locatedVessels });
  }

  // ── Actions the drawer rows call ────────────────────────────────────────────
  centerVessel(mmsi: string): void {
    this.onCenterVessel?.(mmsi);
  }
  takeOffMap(mmsi: string): void {
    this.onTakeOffMap?.(mmsi);
  }
  clearAll(): void {
    this.onClearAll?.();
  }

  snapshot(): VoyageVesselState {
    return this._state$.value;
  }

  private patch(patch: Partial<VoyageVesselState>): void {
    this._state$.next({ ...this._state$.value, ...patch });
  }

  // ── PlaybackController contract — a point list has no transport ─────────────
  togglePlay(): void {}
  play(): void {}
  pause(): void {}
  seek(_index: number): void {}
  stepForward(): void {}
  stepBack(): void {}
  skipToStart(): void {}
  skipToEnd(): void {}
  setSpeed(_n: number): void {}
  toggleLabels(): void {}
  setTypeFilter(_set: Set<string>): void {}
  enterPlayback(_fromIso: string, _toIso: string): Promise<number> {
    return Promise.resolve(0);
  }
  resumeLive(): void {}
  close(): void {
    this.setLocatedVessels([]);
  }
}
